<template>
    <div class="page-padding">
        <div class="header">
            <div>
                <arrow-button color="#aeb5bc" @click="$router.push('/manage/formtemplates')" direction="left">Formulieren</arrow-button>
            </div>
            <div v-if="creatingTemplate" class="inline-flex">
                <q-clear-input v-model="formTemplate.name" type="text" size="extralarge" class="input" @enter="handleCreateTemplate" placeholder="Vul een formuliernaam in" autofocus></q-clear-input>
                <q-button :disabled="formTemplate.name.length === 0" :loading="loadingCreatingTemplate" @click="handleCreateTemplate">Bevestig formuliernaam</q-button>
            </div>
            <h2 v-else>{{ formTemplate.name }}</h2>
        </div>

        <div v-if="!creatingTemplate && (activeLabels.length > 0 || disabledLabels.length > 0)" class="actions-container">
            <div class="left">
                <q-input labelPosition="inside" v-model="searchingValue" variation="blank" size="medium" placeholder="Typ om te zoeken" @input="_handleFilterLabels">Zoekopdracht</q-input>
            </div>
            <div v-if="!isCrowTemplate" class="right">
                <div class="button-container">
                    <q-button size="medium" variation="blank" icon="SortArrows" iconPosition="right" @click="toggleLabelSort">{{ enableDragQuestions ? 'Wijzigingen annuleren' : 'Volgorde wijzigen' }}</q-button>
                </div>
                <div class="button-container">
                    <q-button size="medium" variation="primary" @click="primaryButtonAction" :loading="primaryLoading">{{ enableDragQuestions ? 'Volgorde opslaan' : 'Prestatie toevoegen' }}</q-button>
                </div>
            </div>
        </div>

        <div v-else class="loading-state"></div>

        <q-zerostate
            v-if="!creatingTemplate && (activeLabels.length === 0 && disabledLabels.length === 0) && !loadingLabels"
            class="mt-xxl" 
            img="/statics/img/survey_gradient.svg"
        >
            <div style="max-width: 700px; text-align: center">
                <span>Om te kunnen meten moet je eerst vragen toevoegen aan je formulier. Selecteer een prestatie en bijbehorende vragen of maak een nieuwe vraag aan. </span>
                <q-button class="mt-m" style="margin-inline: auto;" size="medium" variation="primary" @click="handleAddLabel">Prestatie toevoegen</q-button>
            </div>
        </q-zerostate>

        <div v-else class="data-container">
            <div class="show-loader" :class="loadingLabels ? 'show' : 'hide'">
                <div class="center">
                    <div class="loader"></div>
                </div>
            </div>
            <context-rows
                v-if="sortedActiveLabels.length > 0"
                :labels="sortedActiveLabels"
                :allLabels="labels"
                :collapsedRowId="collapsedRowId"
                :dragLabels="enableDragQuestions"
                :dragQuestions="enableDragQuestions"
                :searchingValue="searchingValue"
                :disableEdit="isCrowTemplate"
                dataName="prestatie"
                contextName="formulier"
                questionsOnly
                noDisable
                @toggleShowRow="toggleShowRow"
                @indicatorUpdated="handleIndicatorUpdated"
                @indicatorAdded="handleIndicatorsAdded"
                @indicatorDeleted="handleIndicatorDeleted"
                @indicatorCreated="handleIndicatorCreated"
                @labelUpdated="handleLabelUpdated"
                @labelDeleted="handleLabelDeleted"
                @indicatorOrderUpdated="handleQuestionOrderUpdated"
                @orderUpdated="handleLabelOrderUpdated">
            </context-rows>
            <p v-else-if="!loadingLabels && !creatingTemplate" class="template-rows">Er zijn geen actieve prestaties gevonden</p>

            <div v-if="sortedDisabledLabels.length > 0" class="template-group">
                <h3>Gedeactiveerde prestaties</h3>
                <context-rows
                    class="template-rows"
                    :labels="sortedDisabledLabels"
                    :allLabels="labels"
                    :collapsedRowId="collapsedRowId" 
                    :loadingComponents="loadingComponents" 
                    :searchingValue="searchingValue"
                    :disableToggleRow="enableDragQuestions"
                    questionsOnly
                    dataName="prestatie"
                    @toggleShowRow="toggleShowRow"
                    @labelUpdated="handleLabelUpdated"
                    @labelDeleted="handleLabelDeleted"
                    @indicatorUpdated="handleIndicatorUpdated"
                    @indicatorCreated="handleIndicatorCreated"
                    @indicatorDeleted="handleIndicatorDeleted"
                    disabled
                    noDisable>
                </context-rows>
            </div>
        </div>

        <create-label-modal 
            v-if="showAddLabelModal" 
            :contextName="formTemplateName" 
            :disabledLabels="labels"
            @cancel="showAddLabelModal = false" 
            @created="handleLabelCreated" 
            @added="handleLabelAdded">
        </create-label-modal>
    </div>
</template>

<script>
import ArrowButton from '@/components/ArrowButton.vue';
import gql from 'graphql-tag';
import ContextRows from './ContextRows.vue';
import CreateLabelModal from './CreateLabelModal.vue';
import _ from 'lodash';
import { GET_INDICATORS_WITH_TEMPLATES, GET_FORM_TEMPLATES } from '@/graphql/queries';
import { UPDATE_LABEL, FORMTEMPLATE_REMOVE_LABEL, CREATE_FORMTEMPLATE, FORMTEMPLATE_UPDATE_COMPONENT_ORDER, FORMTEMPLATE_UPDATE_LABEL_ORDER } from '@/graphql/mutations';
import QClearInput from '../../../components/qds/components/QClearInput.vue';

export default {
    name: 'form-template-management',
    components: {
        'arrow-button': ArrowButton,
        ContextRows,
        CreateLabelModal,
        QClearInput
    },
    data() {
        return {
            formTemplate: {
                name: this.$route.query.name || '',
                components: []
            },
            formtemplates: [],
            cachedLabels: [],
            labels: [],
            indicators: [],
            activeLabels: [],
            sortedActiveLabels: [],
            disabledLabels: [],
            sortedDisabledLabels: [],
            enableDragQuestions: false,
            collapsedRowId: null,
            searchingValue: '',
            showAddLabelModal: false,
            loadingLabels: false,
            creatingTemplate: false,
            loadingCreatingTemplate: false,
            primaryLoading: false,
            isCrowTemplate: false, 
            formTemplateName: 'formtemplate'
        }
    },
    methods: {
        getFormTemplate() {
            this.loadingLabels = true;
            const formId = this.$route.params.id;

            this.$apollo.query({
                query: gql`
                    query {
                        formtemplate(id: "${formId}"){
                            id
                            name
                            components {
                                id
                                indicatorId
                            }
                            labels {
                                id
                                name
                                description
                                masterId
                                isMaster
                                status
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache'
            })
            .then(async result => {
                this.formTemplate = result.data.formtemplate;
                this.labels = this.formTemplate.labels || [];

                if(this.formTemplate.name.includes('form_template')) {
                    this.isCrowTemplate = true;
                    this.formTemplate.name = this.$t(`formtemplates.${this.formTemplate.name}`)
                }

                const increment = 100;
                let indicators = [];

                while(true) {
                    const incrementIndicators = await this.getIndicators(increment, indicators.length);

                    indicators = [...indicators, ...incrementIndicators];
                    if(incrementIndicators.length < increment) break
                }

                const orderedIndicators = this.formTemplate.components
                    .map(component => indicators.find(indicator => indicator.id === component.indicatorId))
                    .filter(indicator => indicator)
                this.formTemplate.indicators = orderedIndicators;
                this.indicators = orderedIndicators;

                this.indicatorIds = orderedIndicators.map(i => i.id);

                this.parseLabels();

                this.loadingLabels = false;
            })
            .catch(error => {
                console.log(error)
                this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het ophalen van het formuliertemplate' });
                this.$router.push('/manage/formtemplates');
                this.loadingLabels = false;
            })
        },
        async getFormTemplates() {
            const { id: organisationId } = this.$store.getters.getCurrentOrganisation;
            const formtemplates = await this.$apollo.query({
                query: GET_FORM_TEMPLATES,
                variables: {
                    where: { AND: [
                        { organisationId },
                        { type: "form" },
                        { status__in: ["active", "disabled"] }
                    ]}
                },
                fetchPolicy: 'no-cache'
            })
            .then(result => result.data.formtemplates);

            this.formtemplates = formtemplates;
        },
        async getIndicators(first, skip) {
            const { id: organisationId } = this.$store.getters.getCurrentOrganisation;

            const indicatorIds = this.formTemplate.components.map(component => component.indicatorId)

            try {
                const result = await this.$apollo.query({
                    query: GET_INDICATORS_WITH_TEMPLATES,
                    variables: {
                        organisationId,
                        where: {
                            id__in: indicatorIds
                        },
                        first,
                        skip
                    },
                    fetchPolicy: 'no-cache'
                })

                return result.data.indicators_with_templates

            } catch (error) {
                console.log(error)
                this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het ophalen van de vragen' });
                this.$router.push('/manage/formtemplates');
            }
        },
        parseQuestions(questions) {
            if(!questions) return []
            return questions.map(question => {
                question.enabled = question.enabled ?? true;
                question.required = question.required ?? true;
                question.mandatory = question.mandatory ?? true;
                question.canFillComment = question.canFillComment ?? true;
                question.canFillNotApplicable = question.canFillNotApplicable ?? true;
                if(question.status !== 'active') question.__customStyling = 'color: #757575;';
                else question.__customStyling = null;
                return question
            })
        },
        toggleLabelSort() {
            if(this.enableDragQuestions) {
                this.labels = [ ...this.cachedLabels ];
                this.parseLabels();
                this.enableDragQuestions = false;
            } else {
                this.cachedLabels = _.cloneDeep(this.labels);
                this.enableDragQuestions = true;
                this.collapsedRowId = null;
            }
        },
        toggleShowRow(row) {
            if(this.collapsedRowId !== row.id) {
                this.collapsedRowId = row.id;
                this.scrollToLabel(row.id);
            }
            else this.collapsedRowId = null;
        },
        handleQuestionOrderUpdated({ labelId, indicators: questions }) {
            const questionIndicatorIds = questions.map(question => question.id);
            const indexOfLabelInQuestions = this.indicators.findIndex(indicator => indicator.labelIds[0] === labelId);
            const filteredIndicators = this.indicators.filter(indicator => !questionIndicatorIds.includes(indicator.id));
            const indicatorsBeforeLabel = filteredIndicators.slice(0, indexOfLabelInQuestions);
            const indicatorsAfterLabel = filteredIndicators.slice(indexOfLabelInQuestions, filteredIndicators.length);

            this.indicators = [ ...indicatorsBeforeLabel, ...questions, ...indicatorsAfterLabel ];
            this.parseLabels();
        },
        handleSaveQuestionOrder() {
            const allComponentIds = this.indicators.map(indicator => this.formTemplate.components.find(component => component.indicatorId === indicator.id).id);
            
            this.$apollo.mutate({
                mutation: FORMTEMPLATE_UPDATE_COMPONENT_ORDER,
                variables: {
                    id: this.$route.params.id,
                    componentIds: allComponentIds
                }
            })
            .then(result => {
                this.enableDragQuestions = false;
                return

            })
            .catch(error => {
                this.enableDragQuestions = false;
                return
            })
        },
        handleSaveLabelOrder() {
            const labelIds = this.labels.map(label => label.id);

            this.$apollo.mutate({
                mutation: FORMTEMPLATE_UPDATE_LABEL_ORDER,
                variables: {
                    id: this.$route.params.id,
                    labelIds
                }
            })
            .then(result => {
                this.enableDragQuestions = false;
                return
            })
            .catch(error => {
                this.enableDragQuestions = false;
                return
            })
        },
        handleAddLabel() {
            this.showAddLabelModal = true;
        },
        _handleFilterLabels: _.debounce(function() {
            this.handleFilterLabels()
        }, 250),
        handleFilterLabels() {
            this.sortedActiveLabels = [...this.activeLabels].filter(label => label.name.toLowerCase().includes(this.searchingValue.toLowerCase()))
            this.sortedDisabledLabels = [...this.disabledLabels].filter(label => label.name.toLowerCase().includes(this.searchingValue.toLowerCase()))
        },
        parseLabels() {
            const translatedNames = ['Project_details', 'Crow_project_details'];

            this.labels = this.labels.map(label => {
                if(translatedNames.includes(label.name)) label.name = this.$t(label.name);
                const questions = this.indicators.filter(indicator => indicator.type === 'question' && indicator.labelIds.includes(label.id));

                return {
                    ...label,
                    questions: this.parseQuestions(questions)
                }
            })

            const activeLabels = this.labels.filter(label => label.status === 'active');
            const disabledLabels = this.labels.filter(label => label.status === 'disabled');

            this.activeLabels = [...activeLabels];
            this.sortedActiveLabels = [...activeLabels];
            this.disabledLabels = disabledLabels;
            this.sortedDisabledLabels = disabledLabels;
        },
        handleIndicatorDeleted(indicator) {
            const indicatorIndex = this.indicators.findIndex(_indicator => _indicator.id === indicator.id);
            if(indicatorIndex === -1) return
            this.indicators.splice(indicatorIndex, 1);

            const lastOfLabel = this.indicators.filter(_indicator => _indicator.labelIds[0] === indicator.labelIds[0]).length === 0;
            if(lastOfLabel) {
                const labelIndex = this.labels.findIndex(label => label.id === indicator.labelIds[0]);
                if(labelIndex === -1) return
                this.labels.splice(labelIndex, 1);
            }
            this.parseLabels();
        },
        handleIndicatorCreated(indicator) {
            const templateIds = indicator.formtemplateIds || [];
            const templates = indicator.formtemplates || [];
            templateIds.push(this.formTemplate.id);
            templates.push(this.formTemplate);
            indicator.formtemplateIds = templateIds;
            indicator.formtemplates = templates;
            this.indicators.push(indicator);
            this.parseLabels();
        },
        handleIndicatorUpdated(indicator) {
            const indicatorIndex = this.indicators.findIndex(_indicator => _indicator.id === indicator.id);
            if(indicatorIndex === -1) return

            const indicatorIsInFormtemplate = indicator.formtemplateIds.includes(this.$route.params.id);
            if(indicatorIsInFormtemplate) this.indicators[indicatorIndex] = indicator;
            else {
                this.indicators = this.indicators.filter(_indicator => _indicator.id !== indicator.id);

                const noIndicatorsInLabel = this.indicators.filter(_indicator => _indicator.labelIds[0] === indicator.labelIds[0]).length === 0;
                if(noIndicatorsInLabel) this.labels = this.labels.filter(label => label.id !== indicator.labelIds[0]);
            }

            this.parseLabels();
        },
        handleIndicatorsAdded(indicators) {
            indicators = indicators.map(indicator => {
                const templateIds = indicator.formtemplateIds || [];
                const templates = indicator.formtemplates || [];
                templateIds.push(this.formTemplate.id);
                templates.push(this.formTemplate);
                indicator.formtemplateIds = templateIds;
                indicator.formtemplates = templates;
                return indicator
            });
            this.indicators = [ ...this.indicators, ...indicators ];
            this.parseLabels();
        },
        handleLabelUpdated(label) {
            this.$apollo.mutate({
                mutation: UPDATE_LABEL,
                variables: {
                    id: label.id,
                    name: label.name,
                    description: label.description,
                    status: label.status
                }
            })
            .then(result => {
                this.labels = this.labels.map(_label => {
                    if(_label.id === label.id) return label
                    return _label
                })
                this.parseLabels();
                this.$store.commit('notify', { type: 'success', message: 'De wijzigingen zijn doorgevoerd' })
            })
            .catch(error => {
                this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het opslaan van de wijzigingen' })
            })
        },
        handleLabelDeleted(label) {
            this.labels.map(_label => {
                if (_label.id === label.id) _label.loading = true;
                return _label; 
            });

            this.parseLabels();

            this.$apollo.mutate({
                mutation: FORMTEMPLATE_REMOVE_LABEL,
                variables: {
                    id: this.$route.params.id,
                    labelId: label.id
                }
            })
            .then(result => {
                this.labels = this.labels.filter(_label => _label.id !== label.id);
                this.indicators = this.indicators.filter(indicator => indicator.labelIds[0] !== label.id);
                this.parseLabels();
                this.$store.commit('notify', { type: 'success', message: 'Prestatie is succesvol verwijderd' })
            })
            .catch(error => {
                this.labels.map(_label => {
                    if (_label.id === label.id) _label.loading = false;
                    return _label; 
                });
                this.parseLabels();
                this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het verwijderen van de prestatie' })
            })
        },
        handleCreateTemplate() {
            if(this.labelNameExists) return this.$store.commit('notify', { type: 'info', message: 'Er bestaat al een formtemplate met deze naam' })

            this.loadingCreatingTemplate = true;
            const { id: organisationId } = this.$store.getters.getCurrentOrganisation;

            this.$apollo.mutate({
                mutation: CREATE_FORMTEMPLATE,
                variables: {
                    name: this.formTemplate.name,
                    description: this.formTemplate.description,
                    type: 'form',
                    answerType: 'survey',
                    organisationId,
                    status: 'active'
                }
            })
            .then(result => {
                this.creatingTemplate = false;
                this.loadingCreatingTemplate = false;
                this.$store.commit('notify', { type: 'success', message: 'Formulier is succesvol aangemaakt' });
                this.$router.push(`/manage/formtemplates/${result.data.formtemplate_create.id}?name=${this.formTemplate.name}`);
            })
            .catch(error => {
                console.log(error)
                this.loadingCreatingTemplate = false;
                this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het aanmaken van het formulier' })
            })
        },
        handleLabelCreated(label) {
            this.showAddLabelModal = false;
            this.labels.push(label);
            this.parseLabels();
            this.collapsedRowId = label.id;
            this.scrollToLabel(label.id);
        },
        handleLabelAdded(label, indicators = []) {
            const formtemplateId = this.formTemplate.id || this.$route.params.id;
            indicators = indicators.map(indicator => {
                let templateIds = indicator.formtemplateIds || [];
                let templates = indicator.formtemplates || [];
                if(!templateIds.includes(formtemplateId)) templateIds.push(formtemplateId)
                if(!templates.find(template => template.id === formtemplateId)) templates.push({
                    id: formtemplateId,
                    name: this.formTemplate.name
                })
                return indicator
            })
            this.labels.push(label);
            indicators.forEach(indicator => {
                this.indicators.push(indicator);
            })
            this.parseLabels();
            this.collapsedRowId = label.id;
            this.scrollToLabel(label.id);
        },
        async primaryButtonAction() {
            if(!this.enableDragQuestions) this.handleAddLabel()
            else {

                try {
                    this.primaryLoading = true

                    await this.handleSaveQuestionOrder()
                    await this.handleSaveLabelOrder()

                    this.primaryLoading = false
                    this.$store.commit('notify', { type: 'success', message: 'De aangepaste volgorde is opgeslagen' });
                } catch(error) {
                    this.primaryLoading = false
                    console.log(error);
                    this.$store.commit('notify', { type: 'danger', message: 'Er ging iets fout tijdens het opslaan van de volgorde' })
                }


            }
        },
        async scrollToLabel(rowId) {
            await new Promise(r => setTimeout(r, 300))
            const element = document.getElementById(`row-${rowId}`);
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        handleLabelOrderUpdated(labels) {
            this.labels = labels;
            this.parseLabels()
        }
    },
    computed: {
        labelNameExists: function() {
            if(!this.creatingTemplate) return false
            return this.formtemplates.some(template => template.name.toLowerCase().trim() === this.formTemplate.name.toLowerCase().trim())
        }
    },
    created() {
        this.getFormTemplates();
        const templateId = this.$route.params.id;
        if(templateId === 'create') return this.creatingTemplate = true;
        this.getFormTemplate();
    }
}
</script>

<style lang="scss" scoped>
@import '../../../components/qds/assets/style/_variables.scss';

.page-padding {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.header {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .inline-flex {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .input {
            width: 560px;
        }
    }

}

.actions-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-block: 6px;

    .left {
        min-width: 300px;
    }

    .right {
        display: flex;
        gap: 30px;
    }
}

.template-group {
    padding-top: 36px;
    .template-rows {
        margin-top: 20px;
    }
}

// animations

.show-loader {
    overflow: hidden;
    transition: max-height .3s ease;

    &.show {
        max-height: 120px;
    }
    &.hide {
        max-height: 0;
        .center .loader {
            transition: .2s ease;
            scale: 0.5;
            opacity: 0;
        }
    }
    .center {
        display: flex;
        justify-content: center;
        align-items: center;
        padding-block: 40px;
    }
}

.loader {
    $loader-color: #8a8c8f;
    $loader-size: 40px;
    $loader-border-size: 3px;
    $loader-animation-duration: 700ms;
    @import '../../../components/qds/assets/loaders/loaders.scss';
    @include loader02;
}

</style>